import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Alert, Badge, Button, Card, Col, Row, Spinner, Table,
} from 'react-bootstrap';
import BillingoService from '../../../Services/BillingoService';
import BillingoFailureNote from '../../../Components/Billingo/BillingoFailureNote';
import ExpandableBookingsCell from '../../../Components/Billingo/ExpandableBookingsCell';
import KeyValueRow from '../../../Components/Billingo/KeyValueRow';
import StaleApiNotice from '../../../Components/Billingo/StaleApiNotice';
import { formatAmount } from '../../../Utils/billingoDocuments';
import {
  bookingSubline, formatTripWhen, payerLabel, routeLabel,
} from '../../../Utils/bookingContext';
import { compactAmounts, fullAmounts } from './moneySummary';
import ListPager from '../../../Components/ListPager';
import InvoicingKpi from './InvoicingKpi';
import useDocumentIssuer from '../../../Components/Billingo/useDocumentIssuer';
import useHideBookings from './useHideBookings';
import useBookingDocuments from './useBookingDocuments';
import BookingDocumentsLine from './BookingDocumentsLine';
import useBookingDetails from './useBookingDetails';
import AskAIButton from '../../../Components/ChatWidget/AskAIButton';

// A queue can mix EUR and HUF orders, and adding those together is meaningless.
const totalsByCurrency = (rows, field) => rows.reduce((acc, r) => {
  const cur = r.currency || 'EUR';
  acc[cur] = (acc[cur] || 0) + (Number(r[field]) || 0);
  return acc;
}, {});

// The queue is short and the agent works all of it, so one page holds it.
const QUEUE_LIMIT = 200;

/**
 * How much of the order value the advance already covers. The fill carries
 * the share; the track is the same hue, lighter, so "fully advanced" and
 * "nothing advanced" both read at a glance.
 */
function AdvanceMeter({ group }) {
  const value = Number(group.booking_value) || 0;
  const share = value > 0 ? Math.min(1, (Number(group.advance_total) || 0) / value) : 0;
  const pct = Math.round(share * 100);
  return (
    <div
      className="advance-meter"
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label="Share of the order value already advanced"
      title={`${pct}% of the order value is already on an advance`}
    >
      <span className="advance-meter-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * The final-Számla queue: Completed trips that already carry a document but no
 * final Számla. Every row is an invoice that is legally due.
 *
 * Rows are GROUPS of bookings that share a document, because Billingo deducts a
 * shared Előlegszámla once per Számla. Each row carries the order context the
 * agent would otherwise open the booking for, the documents already issued,
 * and issues straight into the normal flow with the advance deducted. The
 * order cell opens the booking's details, to step through the queue from.
 */
function NeedsFinalSzamla({ refreshKey = 0, onSummaryChange = () => {}, onChanged = () => {} }) {
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const next = await BillingoService.listMissingDocuments({
        missing: 'no_szamla', page, limit: QUEUE_LIMIT,
      });
      setResult(next);
      if (next.summary) onSummaryChange(next.summary);
    } catch (err) {
      // An empty queue would read as "all caught up" — the one wrong answer.
      setResult(null);
      setLoadError(err.response?.data?.message || err.message || 'Could not load the queue.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [page, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const reloadAll = () => { load(); onChanged(); };
  const {
    issue, preparing, error, modal,
  } = useDocumentIssuer({ onIssued: reloadAll });
  const hider = useHideBookings({ onChanged: load });

  const rows = useMemo(() => result?.rows || [], [result]);
  const docs = useBookingDocuments(rows);
  const details = useBookingDetails(rows, reloadAll);
  const remaining = useMemo(() => totalsByCurrency(rows, 'remaining'), [rows]);
  const advanced = useMemo(() => totalsByCurrency(rows, 'advance_total'), [rows]);
  const underpaid = rows.filter((g) => g.paid + 0.005 < g.booking_value).length;

  const issueLabel = (group) => {
    const count = group.bookings.length;
    return `Issue Számla${count > 1 ? ` (${count})` : ''}`;
  };

  if (!loading && !loadError && result && !result.stale && rows.length === 0) {
    return (
      <div className="text-center text-muted py-5">
        <i className="fa fa-circle-check fa-2x mb-2 d-block text-success opacity-75" />
        Every completed trip has its final Számla.
        {modal}
        {hider.modal}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <h6 className="mb-1">Completed trips awaiting a final Számla</h6>
        <div className="text-muted small">
          The trip is rendered and a document exists — the final invoice is due.
          Issuing from here deducts any advance automatically.
        </div>
      </div>

      {rows.length > 0 && (
        <Row className="g-3 mb-3">
          <Col xs={12} sm={6} lg={3}>
            <InvoicingKpi
              label="Groups to finalize"
              value={result.pagination?.total ?? rows.length}
              icon="fa-file-invoice"
              accent="#dc2626"
            />
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <InvoicingKpi
              label="Still to invoice"
              value={compactAmounts(remaining) || '—'}
              title={fullAmounts(remaining)}
              icon="fa-coins"
              accent="#d97706"
            />
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <InvoicingKpi
              label="Already advanced"
              value={compactAmounts(advanced) || '—'}
              title={fullAmounts(advanced)}
              icon="fa-hand-holding-dollar"
              accent="#2563eb"
            />
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <InvoicingKpi
              label="Not fully paid"
              value={underpaid}
              detail={underpaid ? 'check the balance before issuing' : 'all groups fully paid'}
              icon="fa-scale-unbalanced"
              accent="#6366f1"
            />
          </Col>
        </Row>
      )}

      {(error || loadError) && <Alert variant="danger" className="py-2 small">{error || loadError}</Alert>}

      {loading && (
        <div className="text-muted small">
          <Spinner size="sm" animation="border" className="me-2" />
          Loading…
        </div>
      )}

      <StaleApiNotice show={!!result?.stale} detail="the final-Számla queue" />

      {rows.length > 0 && (
        <>
          <Table responsive hover className="align-middle d-none d-md-table">
            <thead className="table-light">
              <tr>
                <th>Order(s)</th>
                <th>Trip</th>
                <th>Documents</th>
                <th className="text-end">Order value</th>
                <th className="text-end">Paid</th>
                <th className="text-end">To invoice</th>
                <th className="text-end" aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((group) => {
                const grouped = group.bookings.length > 1;
                const [first] = group.bookings;
                return (
                  <tr key={group.group_key}>
                    <td>
                      <ExpandableBookingsCell
                        bookings={group.bookings}
                        onToggle={() => details.open(group)}
                        subline={[payerLabel(group), grouped ? `${group.bookings.length} orders on one Számla` : ''].filter(Boolean).join(' · ')}
                      />
                    </td>
                    <td className="small">
                      <div>{formatTripWhen(first.date)}</div>
                      <div
                        className="text-muted text-truncate"
                        style={{ maxWidth: 220 }}
                        title={routeLabel(first, 200)}
                      >
                        {grouped ? `from ${formatTripWhen(group.date, { withTime: false })}` : routeLabel(first)}
                      </div>
                    </td>
                    <td className="small">
                      <BookingDocumentsLine row={group} docs={docs} />
                      {group.advance_total > 0 && <AdvanceMeter group={group} />}
                      {group.has_outside_bookings && (
                        <Badge
                          bg="warning"
                          text="dark"
                          className="fw-normal mt-1"
                          title="A document here also covers orders outside this group — check it is not already deducted elsewhere before issuing"
                        >
                          also covers other orders
                        </Badge>
                      )}
                      {/* Why it is still missing: Billingo already said no.
                          Next to the documents, so it is read before the
                          Issue button is pressed again. */}
                      <BillingoFailureNote failure={group.last_failure} />
                    </td>
                    <td className="text-end">{formatAmount(group.booking_value, group.currency)}</td>
                    <td className="text-end">
                      {formatAmount(group.paid, group.currency)}
                      {group.paid + 0.005 < group.booking_value && (
                        <div className="text-muted" style={{ fontSize: '.7rem' }}>
                          {`${formatAmount(group.booking_value - group.paid, group.currency)} outstanding`}
                        </div>
                      )}
                    </td>
                    <td className="text-end fw-semibold">
                      {formatAmount(group.remaining, group.currency)}
                    </td>
                    <td className="text-end text-nowrap">
                      <AskAIButton
                        refs={group.bookings.map((b) => b.booking_reference)}
                        className="me-1"
                      />
                      <Button
                        size="sm"
                        onClick={() => issue(group)}
                        disabled={preparing === group.group_key}
                      >
                        {preparing === group.group_key ? 'Opening…' : issueLabel(group)}
                      </Button>
                      <Button
                        size="sm"
                        variant="link"
                        className="text-muted ms-1"
                        onClick={() => hider.hide([group])}
                        disabled={hider.busy}
                        title="Hide from the queue for everyone (unhide from Missing documents → Hidden)"
                        aria-label={`Hide ${first.booking_reference}`}
                      >
                        <i className="fa fa-eye-slash" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="d-md-none">
            {rows.map((group) => {
              const grouped = group.bookings.length > 1;
              return (
                <Card key={group.group_key} className="mb-2">
                  <Card.Body className="py-2">
                    <div className="mb-2">
                      <ExpandableBookingsCell
                        bookings={group.bookings}
                        onToggle={() => details.open(group)}
                        subline={[payerLabel(group), grouped ? `${group.bookings.length} orders on one Számla` : bookingSubline(group.bookings[0])].join(' · ')}
                      />
                    </div>
                    <BookingDocumentsLine row={group} docs={docs} />
                    {group.advance_total > 0 && <AdvanceMeter group={group} />}
                    <KeyValueRow label="Order value" value={formatAmount(group.booking_value, group.currency)} />
                    <KeyValueRow label="Paid" value={formatAmount(group.paid, group.currency)} />
                    <KeyValueRow emphasis label="To invoice" value={formatAmount(group.remaining, group.currency)} />
                    <BillingoFailureNote failure={group.last_failure} />
                    <div className="d-flex gap-2 mt-2">
                      <AskAIButton refs={group.bookings.map((b) => b.booking_reference)} />
                      <Button
                        size="sm"
                        className="flex-grow-1"
                        onClick={() => issue(group)}
                        disabled={preparing === group.group_key}
                      >
                        {preparing === group.group_key ? 'Opening…' : issueLabel(group)}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {result?.pagination && result.pagination.pages > 1 && (
        <ListPager
          pagination={result.pagination}
          onPageChange={setPage}
          onLimitChange={() => {}}
        />
      )}

      {modal}
      {hider.modal}
      {details.modal}
    </div>
  );
}

export default NeedsFinalSzamla;
